/**
 * Catalogue template (T3): the behaviour shared by every list page with a
 * filter column (regional transactions and objects, listings, developer offers
 * and developers).
 *
 * - Filters apply on change: a select, checkbox or radio submits the filter
 *   form at once, a text or number field when it loses focus or on Enter, and
 *   a from–to pair (`data-catalog-range`) when focus leaves the pair. Empty
 *   fields are left out of the address so filtered URLs stay short.
 * - On phones (≤ 1024 px) the filter column opens as a bottom sheet from the
 *   toolbar's „Filtry” button: focus is trapped inside, Escape and the
 *   backdrop close it, the page behind does not scroll, and changes apply only
 *   with the sheet's „Pokaż wyniki” button.
 * - The filter column stays sticky only while it fits in the viewport.
 * - A sort select whose options are addresses (`data-catalog-sort-links`)
 *   opens the chosen one; the sortable table headers are plain links.
 * - Row toggles (`data-catalog-row-toggle`) open the detail row they control.
 *
 * Hooks are data attributes only; the visual classes come from the shared
 * components (.ms-filter-panel, .ms-sheet, .ms-list-toolbar).
 */

const PHONE_QUERY = '(max-width: 1024px)';
const STICKY_TOP_PX = 72;
const STICKY_MARGIN_PX = 16;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const phone = window.matchMedia(PHONE_QUERY);

function submitForm(form) {
    const results = document.querySelector('[data-catalog-results]');
    if (results) {
        results.classList.add('ms-is-refetching');
        results.setAttribute('aria-busy', 'true');
    }
    if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
    } else {
        form.dispatchEvent(new Event('submit', { cancelable: true }));
        form.submit();
    }
}

/** Leaves empty fields out of the submitted address. */
function dropEmptyFields(form) {
    Array.prototype.forEach.call(form.elements, (field) => {
        if (!field.name || field.disabled) {
            return;
        }
        const isChoice = field.type === 'checkbox' || field.type === 'radio';
        if (!isChoice && String(field.value).trim() === '') {
            field.disabled = true;
            field.setAttribute('data-catalog-dropped', '');
        }
    });
}

function restoreDroppedFields() {
    document.querySelectorAll('[data-catalog-dropped]').forEach((field) => {
        field.disabled = false;
        field.removeAttribute('data-catalog-dropped');
    });
    document.querySelectorAll('[data-catalog-results].ms-is-refetching').forEach((results) => {
        results.classList.remove('ms-is-refetching');
        results.removeAttribute('aria-busy');
    });
}

function initForms() {
    document.addEventListener('submit', (event) => {
        const form = event.target;
        if (form instanceof HTMLFormElement && form.matches('[data-catalog-form]')) {
            dropEmptyFields(form);
        }
    }, true);

    document.addEventListener('change', (event) => {
        const field = event.target;
        const form = field && field.form;
        if (!form || !form.matches('[data-catalog-form]') || field.matches('[data-catalog-manual]')) {
            return;
        }
        // Inside the phone sheet the visitor applies everything at once.
        if (field.closest('.is-sheet-open')) {
            return;
        }
        // A from–to pair applies when focus leaves the pair, not between its fields.
        // `change` fires before focus moves, so the decision waits for focusout
        // (or, when focus stays put, for the next task).
        const range = field.closest('[data-catalog-range]');
        if (range) {
            range.setAttribute('data-catalog-pending', '');
            window.setTimeout(() => {
                if (range.hasAttribute('data-catalog-pending') && !range.contains(document.activeElement)) {
                    range.removeAttribute('data-catalog-pending');
                    submitForm(form);
                }
            }, 0);
            return;
        }
        submitForm(form);
    });

    document.addEventListener('focusout', (event) => {
        const range = event.target.closest && event.target.closest('[data-catalog-range][data-catalog-pending]');
        if (!range || (event.relatedTarget && range.contains(event.relatedTarget))) {
            return;
        }
        range.removeAttribute('data-catalog-pending');
        const field = range.querySelector('input, select');
        if (field && field.form && !field.closest('.is-sheet-open')) {
            submitForm(field.form);
        }
    });

    // A page restored from the back/forward cache keeps the fields disabled above.
    window.addEventListener('pageshow', restoreDroppedFields);
}

function initSortLinks() {
    document.querySelectorAll('select[data-catalog-sort-links]').forEach((select) => {
        select.addEventListener('change', () => {
            if (select.value) {
                window.location.assign(select.value);
            }
        });
    });
}

function initRowToggles() {
    document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-catalog-row-toggle]');
        if (!toggle) {
            return;
        }
        const target = document.getElementById(toggle.getAttribute('aria-controls') || '');
        if (!target) {
            return;
        }
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        target.hidden = !open;
    });
}

function initStickyColumn(aside) {
    const panel = aside.firstElementChild;
    if (!panel || !aside.hasAttribute('data-catalog-sticky')) {
        return;
    }
    const update = () => {
        const fits = !phone.matches && panel.offsetHeight <= window.innerHeight - STICKY_TOP_PX - STICKY_MARGIN_PX;
        aside.classList.toggle('ms-layout__aside--sticky', fits);
    };
    update();
    window.addEventListener('resize', update);
    aside.addEventListener('toggle', update, true);
}

function initSheet(aside) {
    const openers = Array.prototype.slice.call(document.querySelectorAll('[data-catalog-sheet-open]'))
        .filter((button) => button.getAttribute('aria-controls') === aside.id);
    if (openers.length === 0) {
        return;
    }
    const panel = aside.firstElementChild;
    const title = aside.querySelector('[data-catalog-sheet-title]');
    let backdrop = null;
    let returnFocus = null;

    const focusables = () => Array.prototype.filter.call(
        aside.querySelectorAll(FOCUSABLE),
        (element) => element.offsetParent !== null
    );

    const close = () => {
        if (!aside.classList.contains('is-sheet-open')) {
            return;
        }
        aside.classList.remove('is-sheet-open');
        aside.removeAttribute('role');
        aside.removeAttribute('aria-modal');
        document.documentElement.classList.remove('ms-sheet-lock');
        if (backdrop) {
            backdrop.remove();
            backdrop = null;
        }
        openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        if (returnFocus) {
            returnFocus.focus();
            returnFocus = null;
        }
    };

    const open = (opener) => {
        if (!phone.matches) {
            panel.scrollIntoView({ block: 'start' });
            return;
        }
        returnFocus = opener;
        backdrop = document.createElement('div');
        backdrop.className = 'ms-sheet__backdrop';
        backdrop.setAttribute('data-catalog-sheet-backdrop', '');
        backdrop.addEventListener('click', close);
        document.body.appendChild(backdrop);
        aside.classList.add('is-sheet-open');
        aside.setAttribute('role', 'dialog');
        aside.setAttribute('aria-modal', 'true');
        if (title && title.id) {
            aside.setAttribute('aria-labelledby', title.id);
        }
        document.documentElement.classList.add('ms-sheet-lock');
        openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
        const first = focusables()[0];
        if (first) {
            first.focus();
        }
    };

    openers.forEach((button) => button.addEventListener('click', () => open(button)));
    aside.querySelectorAll('[data-catalog-sheet-close]').forEach((button) => button.addEventListener('click', close));

    aside.addEventListener('keydown', (event) => {
        if (!aside.classList.contains('is-sheet-open')) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const items = focusables();
        if (items.length === 0) {
            return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    });

    const onViewportChange = () => {
        if (!phone.matches) {
            close();
        }
    };
    if (typeof phone.addEventListener === 'function') {
        phone.addEventListener('change', onViewportChange);
    } else if (typeof phone.addListener === 'function') {
        phone.addListener(onViewportChange);
    }
}

function init() {
    initForms();
    initSortLinks();
    initRowToggles();
    document.querySelectorAll('[data-catalog-filters]').forEach((aside) => {
        initStickyColumn(aside);
        initSheet(aside);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
